/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React from "react";
import { TouchableOpacity } from "react-native";
import { TableCell, TableRow } from "@material-ui/core";

import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store/types";
import { ActionCreators } from "actions";

import {
  ExploreLink,
  TranscriptionIcon,
  Text,
  VideoModalLauncherIcon,
} from "UpduoLibrary";
import {
  skaleRed,
  skaleWebTextGray,
  colorWithOpacity,
  upduoLightestGray,
} from "config/UpduoColors";
import {
  formattedTimeString,
  shortFormattedDateAndTimeString,
} from "config/Utilities";
import UserCompleteCell from "./UserCompleteCell";
import { NodeType } from "config/UtilityTypes";
import {
  GetSessionListQuery,
  SkaleSessionType,
} from "graphql/internal/generated";

export default function ActivityRow({
  itemIndex,
  rowData,
  showTranscription,
  onUpdateSession,
}: {
  itemIndex: number;
  rowData: NodeType<GetSessionListQuery, "staffSessions">;
  showTranscription: boolean;
  onUpdateSession: (
    changes: Partial<
      Pick<SkaleSessionType, "isCancelled" | "allowSharing" | "masteryEdited">
    >
  ) => void;
}) {
  const dispatch = useDispatch();

  const transcriptionObject = useSelector(
    (state: RootState) => state.transcriptionObject
  );

  const formattedTime = shortFormattedDateAndTimeString(
    new Date(rowData.created)
  );

  const isIncomplete = rowData.isCancelled || rowData["masteryLevel"] < 1;
  const textColor = colorWithOpacity(skaleWebTextGray, isIncomplete ? 0.5 : 1);
  const sessionDuration = formattedTimeString(rowData.duration);
  return (
    <TableRow
      key={itemIndex}
      hover={true}
      style={{
        height: 80,
        backgroundColor: isIncomplete
          ? colorWithOpacity(skaleRed, 0.03)
          : transcriptionObject && rowData.id === transcriptionObject.id
          ? "#F3F9FE"
          : undefined,
      }}
    >
      <TableCell
        style={{ color: textColor, borderBottomColor: upduoLightestGray }}
      >
        <Text style={{ color: skaleWebTextGray, fontSize: 14 }}>
          {formattedTime}
        </Text>
      </TableCell>
      <TableCell style={{ borderBottomColor: upduoLightestGray }}>
        <ExploreLink
          text={rowData.skill.name}
          to={`/content/lesson/${rowData.skill.id}`}
          isIncomplete={isIncomplete}
        />
      </TableCell>
      {transcriptionObject ? null : (
        <TableCell style={{ borderBottomColor: upduoLightestGray }}>
          <UserCompleteCell
            rowData={rowData}
            isIncomplete={isIncomplete}
            role={"teacher"}
          />
        </TableCell>
      )}
      {transcriptionObject ? null : (
        <TableCell style={{ borderBottomColor: upduoLightestGray }}>
          <UserCompleteCell
            rowData={rowData}
            isIncomplete={isIncomplete}
            role={"learner"}
          />
        </TableCell>
      )}
      <TableCell
        align={"center"}
        style={{ borderBottomColor: upduoLightestGray }}
      >
        <Text style={{ color: skaleWebTextGray, fontSize: 14 }}>
          {sessionDuration}
        </Text>
      </TableCell>
      <TableCell style={{ borderBottomColor: upduoLightestGray }}>
        <VideoModalLauncherIcon
          session={rowData}
          onUpdateSession={onUpdateSession}
        />
      </TableCell>
      {showTranscription ? (
        rowData.hasTranscript ? (
          <TableCell
            align={"center"}
            style={{ borderBottomColor: upduoLightestGray }}
          >
            <TouchableOpacity
              style={{ alignItems: "center" }}
              onPress={() => {
                dispatch(ActionCreators.setTranscriptionObject(rowData));
              }}
            >
              <TranscriptionIcon
                isSelected={
                  transcriptionObject && rowData.id === transcriptionObject.id
                }
              />
            </TouchableOpacity>
          </TableCell>
        ) : (
          <TableCell />
        )
      ) : null}
    </TableRow>
  );
}
